// Checkout of the Czech Impultienda store (vizualniatlas). The root URL opens the main product.
export const checkoutUrl = 'https://vizualniatlas.impultienda.ar'

export { gallery, heroImage } from './media'
export type { GalleryItem } from './media'

export const siteMeta = {
  lang: 'cs',
  title: 'Vizuální atlas biomechaniky cviků — 100 map pro osobní trenéry',
}

export const brand = {
  eyebrow: 'Pro osobní trenéry a instruktory fitness',
  headline: '100 již analyzovaných cviků.',
  headlineAccent: 'Přestaň hledat vysvětlení ve videích a učebnicích.',
  name: 'Vizuální atlas biomechaniky cviků',
  subheadline: '100 vizuálních map + 6 praktických bonusů',
  description:
    'Zapojené svaly, anatomické roviny, správné provedení, časté chyby a varianty – vše přehledně na vizuálních mapách, do kterých se podíváš kdykoli.',
  tagline: 'Vyber. Otevři. Vysvětli s jistotou.',
}

export const heroCta = 'Chci celý atlas'

export const heroNote = 'Okamžitý přístup · PDF k tisku · Jednorázová platba'

export const heroGuarantee = 'Záruka vrácení peněz 7 dní.'

export interface Stat {
  value: string
  label: string
}

export const stats: Stat[] = [
  { value: '100', label: 'Vizuálních map' },
  { value: '100', label: 'Analyzovaných cviků' },
  { value: '3', label: 'Tréninkové oblasti' },
  { value: '6', label: 'Praktických bonusů' },
]

export interface Situation {
  title: string
  text: string
}

export const situationsTitle = 'Poznáváš se?'

export const situationsIntro =
  'Pokud pracuješ jako osobní trenér, minimálně jedna z těchto situací ti bude povědomá.'

export const situations: Situation[] = [
  {
    title: 'Klient se tě zeptá na anatomii.',
    text: 'Cvik znáš, ale jasně vysvětlit, které svaly pracují, nejde vždycky hned.',
  },
  {
    title: 'Vidíš technickou chybu, ale těžko ji vysvětluješ.',
    text: 'Hodil by se ti konkrétní bod, podle kterého pohyb rozložíš a zvolíš správnou korekci.',
  },
  {
    title: 'Ztrácíš čas mezi videi, knihami a poznámkami.',
    text: 'Pokaždé, když si chceš něco ověřit, prohledáváš několik různých zdrojů.',
  },
  {
    title: 'Teorii znáš, ale převést ji do praxe trvá.',
    text: 'Propojit svaly, anatomické roviny a skutečné cviky není vždycky jednoduché.',
  },
]

export const situationsClosing =
  'Nechybí ti znalosti. Chybí ti vizuální systém, do kterého se podíváš, když ho potřebuješ.'

export const comparisonCopy = {
  title: 'Od roztříštěných informací ke 100 přehledně zpracovaným cvikům.',
  oldEyebrow: 'Před',
  oldLabel: 'Bez atlasu',
  atlasEyebrow: 'Po',
  atlasLabel: 'S Vizuálním atlasem',
  closing: 'Méně hledání. Více času pro tvé klienty.',
}

export const comparisonOld: string[] = [
  'Hledáš vysvětlení v různých videích a učebnicích.',
  'Poznámky o svalech a pohybech máš rozházené.',
  'Ztrácíš čas tím, že pořád dokola hledáš stejné informace.',
  'Teorii a cviky si musíš propojovat sám.',
]

export const comparisonAtlas: string[] = [
  '100 cviků analyzovaných a přehledně seřazených.',
  'Svaly, technika a pohyb na jedné mapě.',
  'Časté chyby a varianty vždy po ruce.',
  'Anatomie propojená se skutečnými cviky.',
]

export interface DemoStep {
  number: string
  title: string
  text: string
}

export const demoCopy = {
  title: 'Klient se zeptá. Ty máš hotovou analýzu po ruce.',
  questionLabel: 'Klient se ptá:',
  question: 'Jaké svaly pracují při dřepu?',
}

export const demoSteps: DemoStep[] = [
  {
    number: '01',
    title: 'Vyber',
    text: 'Otevři část Dolní končetiny a najdi mapu dřepu.',
  },
  {
    number: '02',
    title: 'Zkontroluj',
    text: 'Projdi zapojené svaly, techniku a nejčastější chyby.',
  },
  {
    number: '03',
    title: 'Vysvětli',
    text: 'Použij analýzu a vysvětli klientovi vše jasně a konkrétně.',
  },
]

export const galleryCopy = {
  title: 'Podívej se do map.',
  subtitle:
    'Skutečné cviky analyzované vizuálně, s nejdůležitějšími informacemi už přehledně seřazenými.',
  footer: '100 analyzovaných cviků. Jeden systém, připravený k použití.',
  cta: 'Chci 100 vizuálních map',
}

export const systemCopy = {
  title: '100 cviků analyzovaných za tebe.',
  subtitle: 'Kompletní vizuální systém rozdělený do tří tréninkových oblastí.',
  areaLabel: 'Oblast',
  includesTitle: 'Na každé mapě najdeš:',
  closing:
    'Není to další e-book o anatomii. Je to vizuální nástroj, který ti pomůže cviky pochopit a vysvětlit.',
}

export interface Module {
  number: string
  title: string
  detail: string
  summary: string
  description: string
  spine: string
}

export const modules: Module[] = [
  {
    number: '01',
    title: 'Dolní končetiny',
    detail: '40 vizuálních map',
    summary: 'Dřep, mrtvý tah, hip thrust, výpady, leg press a jejich hlavní varianty.',
    description:
      'Zapojené svaly, technika a časté chyby u nejdůležitějších cviků na dolní polovinu těla.',
    spine: 'bg-blue-400',
  },
  {
    number: '02',
    title: 'Horní končetiny',
    detail: '40 vizuálních map',
    summary: 'Tlaky, kliky, shyby, přítahy, stahování kladky a izolované cviky.',
    description: 'Vizuální analýzy nejdůležitějších tlakových a tahových pohybů.',
    spine: 'bg-blue-600',
  },
  {
    number: '03',
    title: 'Core a stabilizace',
    detail: '20 vizuálních map',
    summary: 'Plank, dead bug, bird dog, antirotační cviky a kontrola bederní páteře a pánve.',
    description: 'Stabilizace, zapojené svaly a detaily pohybu.',
    spine: 'bg-blue-900',
  },
]

export const mapIncludes: string[] = [
  'Hlavní a pomocné svaly',
  'Stabilizační svaly',
  'Anatomické roviny a pohyby v kloubech',
  'Správné provedení',
  'Časté chyby a možné korekce',
  'Varianty a praktické tipy',
]

export interface Bonus {
  tag: string
  title: string
  benefit: string
}

export const bonusesCopy = {
  title: 'Navíc dostaneš 6 praktických bonusů.',
  subtitle: 'Další nástroje pro analýzu, opakování a vysvětlování cviků.',
  badge: 'V balíčku',
  closing: 'Všech 6 bonusů je už součástí tvého atlasu.',
}

// Not rendered (the live offer uses the 6 professional kits in data/kitOffer.ts).
export const bonuses: Bonus[] = []

export interface Testimonial {
  initials: string
  quote: string
  author: string
  role: string
  city?: string
}

// Vacío a propósito: solo se cargan testimonios reales de compradores checos.
export const testimonials: Testimonial[] = []

export const guaranteeCopy = {
  title: 'Záruka 7 dní. Nakupuješ bez rizika.',
  paragraphs: [
    'Projdi si mapy a všechny materiály z balíčku.',
    'Pokud během 7 dní od nákupu zjistíš, že atlas nesplňuje tvá očekávání, můžeš požádat o vrácení peněz podle podmínek záruky.',
  ],
  closing: 'Jedna platba. Bez předplatného. Přístup navždy.',
}

export interface FaqItem {
  question: string
  answer: string
}

export const faqTitle = 'Nejčastější otázky'

export interface Offer {
  title: string
  name: string
  summary: string
  includes: string[]
  price: string
  currency: string
  /** ISO code sent to the Meta pixel. */
  currencyCode: string
  cta: string
  note: string
}

export const offer: Offer = {
  title: 'Tvůj kompletní Vizuální atlas. Vše v jednom nákupu.',
  name: 'Vizuální atlas biomechaniky cviků',
  summary: '100 vizuálních map + 6 praktických bonusů',
  includes: [
    '100 analyzovaných cviků',
    '40 map: dolní končetiny',
    '40 map: horní končetiny',
    '20 map: core a stabilizace',
    'Svaly, technika, chyby a varianty',
    'Všech 6 bonusů v balíčku',
    'PDF k tisku',
    'Okamžitý přístup navždy',
  ],
  price: '479',
  currency: 'Kč',
  currencyCode: 'CZK',
  cta: 'Ano, chci celý atlas',
  note: 'Jednorázová platba · Bez předplatného · Záruka 7 dní',
}

export const faqs: FaqItem[] = [
  {
    question: 'Hodí se i pro začínající trenéry?',
    answer:
      'Ano. Mapy pomáhají propojit anatomii se skutečnými cviky a můžou ti sloužit jako opora v profesním růstu.',
  },
  {
    question: 'Můžu ho používat v mobilu přímo při práci?',
    answer:
      'Ano. PDF otevřeš v mobilu, tabletu i počítači, kdykoli si potřebuješ cvik připomenout.',
  },
  {
    question: 'Obsahuje hotové tréninkové plány?',
    answer:
      'Ne. Je to systém vizuální analýzy cviků, ne sbírka personalizovaných tréninkových plánů.',
  },
  {
    question: 'Můžu si mapy vytisknout?',
    answer: 'Ano. Všechny materiály dostaneš ve formátu PDF připraveném k tisku.',
  },
  {
    question: 'Jak atlas dostanu? Platím každý měsíc?',
    answer: `Digitální přístup dostaneš hned po zaplacení. Je to jednorázový nákup za ${offer.price} ${offer.currency}, bez předplatného.`,
  },
  {
    question: 'Co když mi atlas nebude vyhovovat?',
    answer: 'Do 7 dní můžeš požádat o vrácení peněz podle podmínek záruky.',
  },
]

export const finalCtaCopy = {
  title: '100 analyzovaných cviků. Připravených, když je potřebuješ.',
  subtitle:
    'Přestaň pořád dokola hledat stejná vysvětlení. Svaly, technika, časté chyby a varianty – přehledně v jednom Vizuálním atlasu.',
  summary: '100 vizuálních map + 6 bonusů',
  cta: 'Chci celý atlas',
}

export const trustBadges = {
  access: 'Okamžitý přístup',
  payment: 'Jednorázová platba',
  guarantee: 'Záruka 7 dní',
}

// Not rendered (the sticky bar was removed); kept so the unused StickyCta file still compiles.
export const stickyCtaCopy = {
  label: 'Celý atlas + 6 sad',
  cta: 'Chci atlas',
}

export const galleryUi = {
  carousel: 'karusel',
  galleryLabel: 'Galerie vizuálních map',
  imageAlt: (title: string) =>
    `Vizuální mapa: ${title} – svaly, anatomické roviny a nejčastější chyby`,
  previous: 'Předchozí mapa',
  next: 'Další mapa',
  goTo: (title: string) => `Zobrazit: ${title}`,
  hint: (current: number, total: number) =>
    `Posuň nebo použij šipky – ${current} z ${total} ukázkových map`,
}

export const footerCopy = {
  tagline: 'Digitální materiál · Okamžitý přístup · Záruka 7 dní',
  rights: 'Všechna práva vyhrazena.',
  links: [
    { label: 'Obchodní podmínky', href: '#podminky' },
    { label: 'Ochrana osobních údajů', href: '#soukromi' },
    { label: 'Kontakt', href: '#kontakt' },
  ],
}

export const heroImageAlt =
  'Vizuální atlas biomechaniky cviků: 100 vizuálních map + 6 profesionálních sad pro osobní trenéry'
